/**
 * The state behind the Bear Trap day assignment screen.
 *
 * Kept out of the component because it is the part that can be wrong in a way
 * nobody notices. The dirty check in particular decides whether the save button
 * is available at all, and both of its failure modes are bad: stuck disabled
 * loses an assignment the operator typed, stuck enabled trains them to ignore
 * an "unsaved changes" badge that never clears.
 *
 * `trapNumber: null` means taking part in neither trap, and it is deliberately
 * NOT sent as a row. Not taking part is the absence of a decision about which
 * trap, so the server removes the row instead of storing "neither" -- see
 * services/bear/day.ts.
 */

import type { BearDayAssignment, BearLeadPreference, SetBearAssignments } from '@kso/schemas';

/** null = neither trap. */
export type TrapChoice = 1 | 2 | null;

export type AssignmentRow = {
  trapNumber: TrapChoice;
  leadPreference: BearLeadPreference;
};

export type AssignmentState = Map<string, AssignmentRow>;

export const EMPTY_ROW: AssignmentRow = { trapNumber: null, leadPreference: 'AUTO' };

/**
 * A stable fingerprint of what the server holds.
 *
 * The picker re-seeds from the server when this CHANGES, not whenever the
 * response object is new. A refetch that returns the same assignment arrives as
 * a fresh array on every poll, and re-seeding on identity would wipe an
 * operator's half-finished edits for no reason at all -- silently, while they
 * were looking at the roster.
 */
export function assignmentSignature(assignments: readonly BearDayAssignment[]): string {
  return assignments
    .filter((a) => a.trapNumber !== null)
    .map((a) => `${a.playerId}:${a.trapNumber}:${a.leadPreference}`)
    .sort()
    .join('|');
}

export function toAssignmentState(assignments: readonly BearDayAssignment[]): AssignmentState {
  return new Map(
    assignments.map((a) => [
      a.playerId,
      { trapNumber: a.trapNumber, leadPreference: a.leadPreference },
    ]),
  );
}

function assignedEntries(state: AssignmentState): Array<[string, AssignmentRow]> {
  return [...state.entries()].filter(([, row]) => row.trapNumber !== null);
}

/**
 * Compares by content, ignoring members set to neither trap.
 *
 * Those are indistinguishable from members nobody has touched -- both send
 * nothing -- so treating a stray "neither" as a change would leave the form
 * permanently dirty after any accidental click.
 */
export function hasUnsavedChanges(current: AssignmentState, saved: AssignmentState): boolean {
  const a = assignedEntries(current);
  const b = assignedEntries(saved);
  if (a.length !== b.length) return true;

  return !a.every(([playerId, row]) => {
    const other = saved.get(playerId);
    return other?.trapNumber === row.trapNumber && other.leadPreference === row.leadPreference;
  });
}

/** The complete statement of the day's assignment, as the PUT expects it. */
export function toAssignmentPayload(state: AssignmentState): SetBearAssignments {
  return {
    assignments: assignedEntries(state).map(([playerId, row]) => ({
      playerId,
      trapNumber: row.trapNumber as 1 | 2,
      leadPreference: row.leadPreference,
    })),
  };
}

export type AssignmentCounts = { bt1: number; bt2: number; unassigned: number };

export function countAssignments(state: AssignmentState, memberCount: number): AssignmentCounts {
  let bt1 = 0;
  let bt2 = 0;
  for (const row of state.values()) {
    if (row.trapNumber === 1) bt1 += 1;
    if (row.trapNumber === 2) bt2 += 1;
  }
  // Clamped: a member assigned to the day who has since left the alliance is no
  // longer in the roster, and a negative "not taking part" would be nonsense.
  return { bt1, bt2, unassigned: Math.max(0, memberCount - bt1 - bt2) };
}

/**
 * Splits the given members alternately between the traps, strongest first.
 *
 * A STARTING POINT, not a recommendation: it knows nothing about who leads
 * well, who is awake at which hour, or who has a cooldown running. The operator
 * is expected to move people afterwards, which is why it is offered as "split
 * shown evenly" rather than as anything the planner endorses.
 */
export function splitEvenly(
  state: AssignmentState,
  playerIds: readonly string[],
): AssignmentState {
  const next = new Map(state);
  playerIds.forEach((playerId, index) => {
    const current = next.get(playerId) ?? EMPTY_ROW;
    next.set(playerId, {
      trapNumber: index % 2 === 0 ? 1 : 2,
      leadPreference: current.leadPreference,
    });
  });
  return next;
}
